import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import {
  ChevronRightRounded,
  FormatQuoteRounded,
  KeyboardReturnRounded,
  NumbersRounded,
} from '@mui/icons-material';
import {
  alpha,
  AutocompleteRenderOptionState,
  Box,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import Highlighter from 'react-highlight-words';
import { FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import { AddReasonSuggestion } from '../AddPredictions/AddReasonSuggestion';
import { AddTrackedKeyword } from '../AddPredictions/AddTrackedKeyword';
import { ReasonSuggestions } from '../FeedbackDetails/utils/getReasonSuggestionsFromSuggestedReasons';
import { ReasonOrEntityLabelTypeWithGroupName } from './AddPredictionsAutocompleteV2';

interface AutoCompleteOptionProps {
  props: React.HTMLAttributes<HTMLLIElement>;
  option: ReasonOrEntityLabelType;
  state: AutocompleteRenderOptionState;
  feedbackID: string;
  onAfterAddingTrackedKeyword: (
    prediction?: ReasonOrEntityLabelTypeWithGroupName,
  ) => void;
  isAddTrackedKeywordView?: boolean;
  setIsAddTrackedKeywordView: (isAddTrackedKeywordView: boolean) => void;
  isAddReasonView?: boolean;
  setIsAddReasonView: (isAddReasonView: boolean) => void;
  onAfterAddingReasonSuggestion: (
    prediction: ReasonOrEntityLabelTypeWithGroupName,
    hasAddedReasonCandidate: boolean,
  ) => void;
  setSearchTerm: (searchTerm: string) => void;
  reasonSuggestions: ReasonSuggestions[];
}

export const AutoCompleteOption = memo(function AutoCompleteOption({
  props,
  option,
  state,
  feedbackID,
  onAfterAddingTrackedKeyword,
  isAddTrackedKeywordView,
  setIsAddTrackedKeywordView,
  isAddReasonView,
  setIsAddReasonView,
  onAfterAddingReasonSuggestion,
  setSearchTerm,
  reasonSuggestions,
}: AutoCompleteOptionProps) {
  const isReasonCandidateSuggestion =
    option.type === FeedbackLabelType.Reason && !option.category;

  if (isAddTrackedKeywordView) {
    if (state.inputValue === option.displayName) {
      return (
        <AddTrackedKeyword
          feedbackID={feedbackID}
          option={option}
          onCancel={() => {
            setSearchTerm('');
            setIsAddTrackedKeywordView(false);
          }}
          onAfterAddingTrackedKeyword={onAfterAddingTrackedKeyword}
        />
      );
    }
    return null;
  }

  if (isAddReasonView) {
    if (state.inputValue === option.displayName) {
      return (
        <AddReasonSuggestion
          option={option}
          onCancel={() => {
            setSearchTerm('');
            setIsAddReasonView(false);
          }}
          onAfterAddingReasonSuggestion={onAfterAddingReasonSuggestion}
          relatedRCs={
            reasonSuggestions.find(
              (reasonSuggestion) => reasonSuggestion.label.id === option.id,
            )?.relatedRCs
          }
        />
      );
    }
    return null;
  }

  const renderPrimaryText = () => {
    if (isReasonCandidateSuggestion) {
      return (
        <Stack spacing={1} direction="row" alignItems="center">
          <Box
            sx={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              borderStyle: 'dashed',
              color: 'text.disabled',
              aspectRatio: 1,
            }}
          />
          <Stack direction="column">
            <Typography variant="caption" color="primary">
              Create new Reason
            </Typography>
            <Highlighter
              textToHighlight={option.displayName}
              searchWords={[state.inputValue]}
              autoEscape={true}
              highlightTag={({ children }) => (
                <strong className="highlighted-text">{children}</strong>
              )}
            />
          </Stack>
        </Stack>
      );
    }

    if (option.type === FeedbackLabelType.Reason) {
      return (
        <Stack spacing={1} direction="row" alignItems="center">
          <Box
            sx={{
              aspectRatio: 1,
            }}
          >
            <CategoryIcon category={option.category ?? null} />
          </Box>
          <Box>
            <FormatQuoteRounded color="disabled" />
            <Highlighter
              textToHighlight={option.displayName}
              searchWords={[state.inputValue]}
              autoEscape={true}
              highlightTag={({ children }) => (
                <strong className="highlighted-text">{children}</strong>
              )}
            />
          </Box>
        </Stack>
      );
    }

    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            borderStyle: 'dashed',
            color: 'text.disabled',
            boxSizing: 'content-box',
          }}
        />
        <NumbersRounded color="disabled" />
        <Highlighter
          textToHighlight={option.displayName}
          searchWords={[state.inputValue]}
          autoEscape={true}
          highlightTag={({ children }) => (
            <strong className="highlighted-text">{children}</strong>
          )}
        />
      </Stack>
    );
  };

  return (
    <ListItem
      key={option.id}
      disablePadding
      {...props}
      style={{
        ...props.style,
        padding: 0,
      }}
      tabIndex={1}
      sx={{
        '&:hover .IconContainer': {
          '& .KeyboardIcon': {
            display: 'flex',
          },
          '& .ChevronRight': {
            display: 'none',
          },
        },
      }}
      secondaryAction={
        <>
          <Box component="span" className="IconContainer">
            <Box
              bgcolor={(theme) => alpha(theme.palette.primary.main, 0.16)}
              px={1}
              className="KeyboardIcon"
              borderRadius={1}
              sx={{
                display: 'none',
              }}
            >
              <KeyboardReturnRounded
                fontSize="small"
                sx={{
                  color: (theme) => theme.palette.primary.main,
                }}
              />
            </Box>
            {option.type === FeedbackLabelType.Entity && (
              <ChevronRightRounded
                fontSize="small"
                color="disabled"
                className="ChevronRight"
                sx={{
                  display: 'flex',
                }}
              />
            )}
          </Box>
        </>
      }
    >
      <ListItemButton>
        <ListItemText primary={renderPrimaryText()}></ListItemText>
      </ListItemButton>
    </ListItem>
  );
});
